import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import BaseSelect from '@/components/base/BaseSelect';
import { Bell, Globe, Shield, Save, Settings as SettingsIcon, Eye, ArrowRight, CheckCircle, User, Calendar } from 'lucide-react';
import { CheckboxLabel } from '@/components/base/Checkbox';

const allRoles = [
  { value: 'operator' as const, label: 'Operator', desc: 'Full platform access', icon: 'ri-admin-line' },
  { value: 'account_manager' as const, label: 'Account Manager', desc: 'Leads & partners', icon: 'ri-user-star-line' },
  { value: 'partner_principal' as const, label: 'Partner Principal', desc: 'Partner portal', icon: 'ri-building-3-line' },
  { value: 'partner_staff' as const, label: 'Partner Staff', desc: 'Limited portal', icon: 'ri-user-add-line' },
  { value: 'read_only' as const, label: 'Read Only', desc: 'Dashboard & reports', icon: 'ri-eye-line' },
];

export default function SettingsPage() {
  const { user, previewRole, setPreviewRole, effectiveRole } = useAuth();

  // Sender profile
  const [senderName, setSenderName] = useState('');
  const [senderTitle, setSenderTitle] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [senderSignature, setSenderSignature] = useState('');
  const [senderSaved, setSenderSaved] = useState(false);

  // Google Calendar
  const [googleConnected, setGoogleConnected] = useState(false);

  useEffect(() => {
    if (!isSupabaseConfigured || !user?.id) return;
    supabase
      .from('profiles')
      .select('sender_name, sender_title, sender_phone, sender_signature, google_calendar_connected')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (!data) return;
        setSenderName(data.sender_name || '');
        setSenderTitle(data.sender_title || '');
        setSenderPhone(data.sender_phone || '');
        setSenderSignature(data.sender_signature || '');
        setGoogleConnected(!!data.google_calendar_connected);
      });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoogleConnect = async () => {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return;
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string;
    const redirectUri = `https://vtcltcyublsvhrrnxbsh.supabase.co/functions/v1/google_oauth_callback`;
    const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    url.searchParams.set('client_id', clientId);
    url.searchParams.set('redirect_uri', redirectUri);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('scope', 'https://www.googleapis.com/auth/calendar.events');
    url.searchParams.set('access_type', 'offline');
    url.searchParams.set('prompt', 'consent');
    url.searchParams.set('state', authUser.id);
    window.location.href = url.toString();
  };

  const handleGoogleDisconnect = async () => {
    if (!isSupabaseConfigured || !user?.id) return;
    await supabase
      .from('profiles')
      .update({ google_calendar_connected: false, google_refresh_token: null })
      .eq('id', user.id);
    setGoogleConnected(false);
  };

  const handleSenderSave = async () => {
    if (!isSupabaseConfigured || !user?.id) return;
    await supabase
      .from('profiles')
      .update({ sender_name: senderName, sender_title: senderTitle, sender_phone: senderPhone, sender_signature: senderSignature })
      .eq('id', user.id);
    setSenderSaved(true);
    setTimeout(() => setSenderSaved(false), 2000);
  };

  const [notifications, setNotifications] = useState({
    email: true,
    whatsapp: false,
    newLead: true,
    dispute: true,
    capAlert: true,
  });
  const [language, setLanguage] = useState('en');
  const [saved, setSaved] = useState(false);

  const isAdmin = user?.role === 'operator';

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Settings</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>Platform configuration</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Role Preview */}
        {isAdmin && (
          <div className="rounded-xl p-5 lg:col-span-2 animate-fade-up" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
            <h2 className="text-sm font-semibold flex items-center gap-2 mb-5" style={{ color: 'var(--text-secondary)' }}>
              <Eye size={16} style={{ color: 'var(--accent)' }} />
              Role Preview
            </h2>
            <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
              Preview the platform as any role without logging out. Your admin access remains active.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {allRoles.map(role => {
                const active = effectiveRole === role.value;
                const isPreview = previewRole === role.value;
                return (
                  <button
                    key={role.value}
                    onClick={() => {
                      if (role.value === 'operator') {
                        setPreviewRole(null);
                      } else {
                        setPreviewRole(role.value);
                      }
                    }}
                    className={`relative text-left p-4 rounded-xl border transition-all duration-300 group hover-card${active ? ' plan-btn-active' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-[13px] font-semibold" style={{ color: active ? 'var(--accent)' : 'var(--text-secondary)' }}>
                          {role.label}
                        </p>
                        <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{role.desc}</p>
                      </div>
                      {isPreview && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border" style={{ background: 'var(--accent-dim)', color: 'var(--accent)', borderColor: 'var(--accent-glow)' }}>
                          Previewing
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex items-center gap-1 text-[11px] transition-colors" style={{ color: 'var(--text-muted)' }}>
                      <span>Preview this view</span>
                      <ArrowRight size={11} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Afzendergegevens */}
        <div className="rounded-xl p-5 lg:col-span-2" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
          <h2 className="text-sm font-semibold flex items-center gap-2 mb-5" style={{ color: 'var(--text-secondary)' }}>
            <User size={16} style={{ color: 'var(--text-muted)' }} />
            Afzendergegevens
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            {[
              { label: 'Naam', value: senderName, set: setSenderName, placeholder: 'bijv. Jan de Vries' },
              { label: 'Functietitel', value: senderTitle, set: setSenderTitle, placeholder: 'bijv. Vloerspecialist' },
              { label: 'Telefoonnummer', value: senderPhone, set: setSenderPhone, placeholder: 'bijv. +31 6 12345678' },
              { label: 'Aanhef', value: senderSignature, set: setSenderSignature, placeholder: 'bijv. Met vriendelijke groet' },
            ].map(field => (
              <div key={field.label}>
                <label className="text-[11px] font-medium uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--text-muted)' }}>{field.label}</label>
                <input
                  type="text"
                  value={field.value}
                  onChange={e => field.set(e.target.value)}
                  placeholder={field.placeholder}
                  className="w-full px-3 py-2 text-sm rounded-lg outline-none"
                  style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleSenderSave}
              className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg transition-all"
              style={{ background: senderSaved ? 'var(--success-bg)' : 'var(--accent)', color: senderSaved ? 'var(--success-text)' : '#fff' }}
            >
              {senderSaved ? <><CheckCircle size={14} /> Opgeslagen</> : <><Save size={14} /> Opslaan</>}
            </button>
          </div>
        </div>

        {/* Google Calendar */}
        <div className="rounded-xl p-5" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
          <h2 className="text-sm font-semibold flex items-center gap-2 mb-4" style={{ color: 'var(--text-secondary)' }}>
            <Calendar size={16} style={{ color: 'var(--text-muted)' }} />
            Google Agenda
          </h2>
          {googleConnected ? (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full"
                  style={{ background: 'var(--success-bg)', color: 'var(--success-text)', border: '1px solid var(--success-border)' }}>
                  <CheckCircle size={11} /> Google Agenda gekoppeld
                </span>
              </div>
              <button
                onClick={handleGoogleDisconnect}
                className="px-3 py-1.5 text-xs rounded-lg border transition-all"
                style={{ color: 'var(--danger-text)', borderColor: 'var(--danger-border)', background: 'var(--danger-bg)' }}
              >
                Ontkoppelen
              </button>
            </div>
          ) : (
            <div>
              <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
                Synchroniseer afspraken automatisch met je Google Agenda.
              </p>
              <button
                onClick={handleGoogleConnect}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all"
                style={{ background: 'var(--accent)', color: '#fff' }}
              >
                <Calendar size={14} /> Koppel Google Agenda
              </button>
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="rounded-xl p-5" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
          <h2 className="text-sm font-semibold flex items-center gap-2 mb-5" style={{ color: 'var(--text-secondary)' }}>
            <Bell size={16} style={{ color: 'var(--text-muted)' }} />
            Notifications
          </h2>
          <div className="space-y-3">
            {[
              { key: 'email', label: 'Email notifications', desc: 'Receive updates via email' },
              { key: 'whatsapp', label: 'WhatsApp notifications', desc: 'Receive updates via WhatsApp' },
              { key: 'newLead', label: 'New lead alerts', desc: 'Alert when a new lead is assigned' },
              { key: 'dispute', label: 'Dispute alerts', desc: 'Alert when a dispute is raised' },
              { key: 'capAlert', label: 'Cap approaching alerts', desc: 'Alert when an offer is near cap limit' },
            ].map(item => (
              <CheckboxLabel
                key={item.key}
                checked={notifications[item.key as keyof typeof notifications]}
                onChange={val => setNotifications(prev => ({ ...prev, [item.key]: val }))}
                label={item.label}
                description={item.desc}
              />
            ))}
          </div>
        </div>

        {/* General */}
        <div className="rounded-xl p-5" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
          <h2 className="text-sm font-semibold flex items-center gap-2 mb-5" style={{ color: 'var(--text-secondary)' }}>
            <Globe size={16} style={{ color: 'var(--text-muted)' }} />
            General
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-[11px] font-medium uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Language</label>
              <BaseSelect
                value={language}
                onChange={val => setLanguage(val)}
                options={[
                  { value: 'en', label: 'English' },
                  { value: 'nl', label: 'Nederlands' },
                ]}
              />
            </div>
            <div>
              <label className="text-[11px] font-medium uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Timezone</label>
              <BaseSelect
                value="Europe/Amsterdam (CET)"
                onChange={() => {}}
                options={[
                  { value: 'Europe/Amsterdam (CET)', label: 'Europe/Amsterdam (CET)' },
                  { value: 'America/New_York (EST)', label: 'America/New York (EST)' },
                  { value: 'America/Los_Angeles (PST)', label: 'America/Los Angeles (PST)' },
                ]}
              />
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="rounded-xl p-5" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
          <h2 className="text-sm font-semibold flex items-center gap-2 mb-5" style={{ color: 'var(--text-secondary)' }}>
            <Shield size={16} style={{ color: 'var(--text-muted)' }} />
            Security
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Two-factor authentication</p>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Add an extra layer of security</p>
              </div>
              <button
                className="px-3 py-1.5 text-xs rounded-lg border transition-all hover-surface"
                style={{ color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }}
              >
                Enable
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Session management</p>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Manage active sessions</p>
              </div>
              <button
                className="px-3 py-1.5 text-xs rounded-lg border transition-all hover-surface"
                style={{ color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }}
              >
                View
              </button>
            </div>
          </div>
        </div>

        {/* API Keys */}
        <div className="rounded-xl p-5" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
          <h2 className="text-sm font-semibold flex items-center gap-2 mb-5" style={{ color: 'var(--text-secondary)' }}>
            <SettingsIcon size={16} style={{ color: 'var(--text-muted)' }} />
            API & Integrations
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Platform API keys</p>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Manage access tokens</p>
              </div>
              <button
                className="px-3 py-1.5 text-xs rounded-lg border transition-all hover-surface"
                style={{ color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }}
              >
                Manage
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Webhook endpoints</p>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Configure event webhooks</p>
              </div>
              <button
                className="px-3 py-1.5 text-xs rounded-lg border transition-all hover-surface"
                style={{ color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }}
              >
                Configure
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => {
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
          }}
          className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-xl transition-all"
          style={{ background: saved ? 'var(--success-bg)' : 'var(--accent)', color: saved ? 'var(--success-text)' : '#fff' }}
        >
          {saved ? <CheckCircle size={14} /> : <Save size={14} />}
          {saved ? 'Saved!' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}